import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import Icon from '../../components/AppIcon';

const STATUS_COLORS = {
  PROCESSING: 'bg-warning/10 text-warning',
  APPROVED: 'bg-success/10 text-success',
  REJECTED: 'bg-error/10 text-error',
  DISPUTED: 'bg-primary/10 text-primary',
};

const TABS = [
  { key: 'PROCESSING', label: 'Processing', icon: 'Clock' },
  { key: 'APPROVED', label: 'Approved', icon: 'CheckCircle' },
  { key: 'REJECTED', label: 'Rejected', icon: 'XCircle' },
  { key: 'DISPUTED', label: 'Disputed', icon: 'AlertTriangle' },
];

const OnboardingApprovalPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [onboardings, setOnboardings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('PROCESSING');
  const [selectedItem, setSelectedItem] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState('');
  const [actionForm, setActionForm] = useState({ mcc: '', domain: '', merchant_id: '', comment: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchOnboardings(); }, [activeTab]);

  const fetchOnboardings = async () => {
    setLoading(true); setError('');
    try {
      const params = { status: activeTab };
      const response = await apiService?.listOnboarding(params);
      const data = response?.data;
      const list = data?.details || data?.data || data?.onboardings || data?.items || data;
      setOnboardings(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load onboarding list');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReview = async (item) => {
    setSelectedItem(item);
    setReviewData(null);
    setAction('');
    setActionForm({ mcc: '', domain: '', merchant_id: '', comment: '' });
    setShowModal(true);
    setReviewLoading(true);
    try {
      const response = await apiService?.getOnboardingList(item?.registration_id || item?.id);
      const data = response?.data;
      // API returns { details: [ { business_details, owners_details, bank_details, ... } ] }
      const record = Array.isArray(data?.details) ? data?.details?.[0] : (data?.details || data || null);
      setReviewData(record);
    } catch (err) {
      setReviewData(null);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleSubmitAction = async () => {
    if (!action) return;
    setSubmitting(true); setError('');
    try {
      const payload = { action };
      if (action === 'APPROVED') {
        payload.mcc = actionForm?.mcc;
        payload.domain = actionForm?.domain;
        payload.merchant_id = actionForm?.merchant_id;
      } else {
        payload.comment = actionForm?.comment;
      }
      await apiService?.submitOnboardingDecision(selectedItem?.registration_id || selectedItem?.id, payload);
      setSuccess(`Application ${action?.toLowerCase()} successfully!`);
      setShowModal(false);
      setAction('');
      setActionForm({ mcc: '', domain: '', merchant_id: '', comment: '' });
      fetchOnboardings();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err?.response?.data?.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  const renderField = (label, value) => (
    <div className="flex justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
      <span className="text-text-secondary font-medium min-w-[140px]">{label}</span>
      <span className="text-text-primary text-right break-all">{value || '-'}</span>
    </div>
  );

  const renderDocLink = (label, url) => (
    <div className="flex justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
      <span className="text-text-secondary font-medium min-w-[140px]">{label}</span>
      <span className="text-right">
        {url ? (
          <a href={url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">View</a>
        ) : (
          <span className="text-text-primary">-</span>
        )}
      </span>
    </div>
  );

  const bizDetails = reviewData?.business_details;
  const ownersDetails = Array.isArray(reviewData?.owners_details) ? reviewData?.owners_details : [];
  const bankDetails = reviewData?.bank_details;

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">Onboarding Approval</h1>
              <p className="text-text-secondary mt-1">Review and approve merchant applications</p>
            </div>
            <button onClick={fetchOnboardings} className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted">
              <Icon name="RefreshCw" size={16} className="text-text-secondary" />
              <span>Refresh</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center border-b border-border mb-5">
            {TABS?.map(tab => (
              <button
                key={tab?.key}
                onClick={() => setActiveTab(tab?.key)}
                className={`flex items-center space-x-2 px-5 py-3 text-sm font-medium border-b-2 transition-smooth -mb-px ${
                  activeTab === tab?.key
                    ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                }`}
              >
                <Icon name={tab?.icon} size={15} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {['Registration ID', 'Trading Name', 'Steps Completed', 'Status', 'Actions']?.map(col => (
                      <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide whitespace-nowrap">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} className="text-center py-12">
                      <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto" />
                    </td></tr>
                  ) : onboardings?.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-12">
                      <div className="flex flex-col items-center space-y-2">
                        <Icon name="Inbox" size={32} className="text-text-secondary" />
                        <p className="text-text-secondary text-sm">No {activeTab?.toLowerCase()} applications found</p>
                      </div>
                    </td></tr>
                  ) : (
                    onboardings?.map((item, i) => (
                      <tr key={item?.registration_id || i} className="border-b border-border last:border-0 hover:bg-muted/30 transition-smooth">
                        <td className="px-4 py-3 text-sm font-mono text-text-primary">{item?.registration_id || item?.id || '-'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-text-primary">{item?.business_details?.trading_name || item?.trading_name || item?.business_name || item?.name || '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-1">
                            {[1, 2, 3]?.map(step => (
                              <div key={step} className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                (item?.steps_completed || item?.last_completed_step || 0) >= step
                                  ? 'bg-success text-success-foreground'
                                  : 'bg-muted text-text-secondary'
                              }`}>{step}</div>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            STATUS_COLORS?.[item?.completion_status || item?.status] || 'bg-muted text-text-secondary'
                          }`}>{item?.completion_status || item?.status || '-'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => handleViewReview(item)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90">
                            <Icon name="Eye" size={12} />
                            <span>Review</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
      {/* Review Modal */}
      {showModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border rounded-xl w-full max-w-2xl shadow-elevated max-h-[92vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Review Application</h3>
                <p className="text-xs text-text-secondary mt-0.5">{selectedItem?.registration_id || selectedItem?.id}</p>
              </div>
              <button onClick={() => { setShowModal(false); setAction(''); setError(''); }}
                className="p-1.5 rounded hover:bg-muted">
                <Icon name="X" size={20} className="text-text-secondary" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
              {reviewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Section 1: Business Information */}
                  <div className="bg-muted/40 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="Building2" size={16} className="text-primary" />
                      <h4 className="text-sm font-semibold text-text-primary">Business Information</h4>
                    </div>
                    <div className="space-y-0">
                      {renderField('Registered Name', bizDetails?.registered_name)}
                      {renderField('Trading Name', bizDetails?.trading_name)}
                      {renderField('Business Type', bizDetails?.business_type)}
                      {renderField('License Type', bizDetails?.business_license_type)}
                      {renderField('Industry Sector', bizDetails?.industry_sector)}
                      {renderField('Physical Address', bizDetails?.physical_address)}
                      {renderField('City', bizDetails?.city)}
                      {renderField('Postal Code', bizDetails?.postal_code)}
                      {renderDocLink('KRA PIN Certificate', bizDetails?.kra_pin_certificate)}
                      {renderDocLink('Business Registration Cert', bizDetails?.business_registration_cert)}
                      {renderDocLink('Business License File', bizDetails?.business_license_file)}
                      {Array.isArray(bizDetails?.additional_documents) && bizDetails?.additional_documents?.filter(Boolean)?.length > 0 && (
                        <div className="flex justify-between text-sm py-1.5 border-b border-border/50 last:border-0">
                          <span className="text-text-secondary font-medium min-w-[140px]">Additional Documents</span>
                          <div className="flex flex-wrap gap-1 justify-end">
                            {bizDetails?.additional_documents?.filter(Boolean)?.map((doc, idx) => (
                              <a key={idx} href={doc} target="_blank" rel="noreferrer"
                                className="text-xs text-primary underline">
                                Document {idx + 1}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 2: Owners Information */}
                  <div className="bg-muted/40 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="Users" size={16} className="text-primary" />
                      <h4 className="text-sm font-semibold text-text-primary">Owners Information</h4>
                    </div>
                    {ownersDetails?.length > 0 ? (
                      ownersDetails?.map((owner, idx) => (
                        <div key={idx} className={`${idx > 0 ? 'mt-4 pt-4 border-t border-border' : ''}`}>
                          {ownersDetails?.length > 1 && (
                            <p className="text-xs font-semibold text-primary mb-2">Owner {idx + 1}</p>
                          )}
                          <div className="space-y-0">
                            {renderField('First Name', owner?.first_name)}
                            {renderField('Last Name', owner?.last_name)}
                            {renderField('Email', owner?.email)}
                            {renderField('Phone', owner?.phone)}
                            {renderField('National ID / Passport', owner?.national_id_passport)}
                            {renderField('Address', owner?.address)}
                            {renderDocLink('National ID / Passport Copy', owner?.national_id_passport_copy)}
                            {renderDocLink('Facial / Passport Photo', owner?.facial_passport_photo)}
                            {renderDocLink('Proof of Address', owner?.proof_of_address)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-text-secondary">No owner information available</p>
                    )}
                  </div>

                  {/* Section 3: Bank Details */}
                  <div className="bg-muted/40 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Icon name="Landmark" size={16} className="text-primary" />
                      <h4 className="text-sm font-semibold text-text-primary">Bank Details</h4>
                    </div>
                    <div className="space-y-0">
                      {renderField('Bank Name', bankDetails?.bank_name)}
                      {renderField('Account Number', bankDetails?.account_number)}
                      {renderField('Branch Code', bankDetails?.branch_code)}
                      {renderField('SWIFT Code', bankDetails?.swift_code)}
                      {renderDocLink('Bank Statement', bankDetails?.bank_statement)}
                    </div>
                  </div>
                </>
              )}

              {/* Action Selection */}
              <div>
                <label className="text-sm font-medium text-text-primary block mb-2">Decision</label>
                <div className="grid grid-cols-3 gap-2">
                  {['APPROVED', 'REJECTED', 'DISPUTED']?.map(a => (
                    <button key={a} onClick={() => setAction(a)}
                      className={`py-2 rounded-lg text-sm font-medium transition-smooth ${
                        action === a
                          ? a === 'APPROVED' ? 'bg-success text-success-foreground'
                            : a === 'REJECTED' ? 'bg-error text-error-foreground'
                            : 'bg-warning text-warning-foreground' :'border border-border text-text-secondary hover:bg-muted'
                      }`}>{a}</button>
                  ))}
                </div>
              </div>

              {/* Action Form */}
              {action === 'APPROVED' && (
                <div className="space-y-3">
                  {[['mcc', 'MCC Code'], ['domain', 'Domain'], ['merchant_id', 'Merchant ID']]?.map(([field, label]) => (
                    <div key={field}>
                      <label className="text-sm font-medium text-text-primary block mb-1">{label} *</label>
                      <input type="text" value={actionForm?.[field]} onChange={e => setActionForm(p => ({ ...p, [field]: e?.target?.value }))}
                        placeholder={label} className={inputClass} />
                    </div>
                  ))}
                </div>
              )}
              {(action === 'REJECTED' || action === 'DISPUTED') && (
                <div>
                  <label className="text-sm font-medium text-text-primary block mb-1">Comment *</label>
                  <textarea value={actionForm?.comment} onChange={e => setActionForm(p => ({ ...p, comment: e?.target?.value }))}
                    placeholder="Provide reason for this decision..."
                    rows={3}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
                </div>
              )}

              {error && (
                <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertCircle" size={14} className="text-error" />
                  <p className="text-xs text-error">{error}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex space-x-3 px-6 py-4 border-t border-border flex-shrink-0">
              <button onClick={() => { setShowModal(false); setAction(''); setError(''); }}
                className="flex-1 px-4 py-2 border border-border text-text-primary rounded-lg text-sm hover:bg-muted">
                Cancel
              </button>
              <button onClick={handleSubmitAction} disabled={!action || submitting}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center space-x-2">
                {submitting ? <><Icon name="Loader2" size={14} className="animate-spin" /><span>Processing...</span></> : <span>Submit Decision</span>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingApprovalPage;
